import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, LogOut, RefreshCw, Trash2 } from "lucide-react";
import { LogoMark } from "../components/Logo";
import { useLang } from "../i18n";
import { serviceName } from "../siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "9cut_admin_token";

const statusColor = {
    pending: "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
    confirmed: "border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF]",
    completed: "border-emerald-400/40 bg-emerald-400/10 text-emerald-400",
    cancelled: "border-white/15 bg-white/5 text-[#8E8EA0]",
};

export default function Admin() {
    const { lang, t } = useLang();
    const a = t.admin;
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [password, setPassword] = useState("");
    const [loginErr, setLoginErr] = useState("");
    const [bookings, setBookings] = useState(null);
    const [filter, setFilter] = useState("all");
    const [blocked, setBlocked] = useState([]);
    const [offDate, setOffDate] = useState("");

    const load = useCallback(
        async (tk) => {
            try {
                const res = await axios.get(`${API}/admin/bookings`, {
                    headers: { Authorization: `Bearer ${tk}` },
                });
                setBookings(res.data);
                const off = await axios.get(`${API}/blocked-dates`);
                setBlocked(off.data.dates || []);
            } catch {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
            }
        },
        []
    );

    useEffect(() => {
        if (token) load(token);
    }, [token, load]);

    const login = async () => {
        setLoginErr("");
        try {
            const res = await axios.post(`${API}/admin/login`, { password });
            localStorage.setItem(TOKEN_KEY, res.data.token);
            setToken(res.data.token);
        } catch {
            setLoginErr(a.wrongPw);
        }
    };

    const act = async (id, status) => {
        await axios.patch(
            `${API}/admin/bookings/${id}`,
            { status },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        load(token);
    };

    const del = async (id) => {
        await axios.delete(`${API}/admin/bookings/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        load(token);
    };

    const addOff = async () => {
        if (!offDate) return;
        await axios.post(
            `${API}/admin/blocked-dates`,
            { date: offDate },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setOffDate("");
        load(token);
    };

    const removeOff = async (d) => {
        await axios.delete(`${API}/admin/blocked-dates/${d}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        load(token);
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setBookings(null);
    };

    if (!token) {
        return (
            <div className="hero-radial flex min-h-screen items-center justify-center px-4" data-testid="admin-login-page">
                <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#13131A]/90 p-8 backdrop-blur-xl">
                    <div className="flex items-center gap-3">
                        <LogoMark size={40} />
                        <div>
                            <h1 className="font-display text-lg font-bold">{a.title}</h1>
                            <p className="text-xs text-[#8E8EA0]">{a.sub}</p>
                        </div>
                    </div>
                    <input
                        data-testid="admin-login-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && login()}
                        placeholder={a.passwordPh}
                        className="mt-7 w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-[#F4F4F6] outline-none focus:border-[#FFB800]"
                    />
                    {loginErr && (
                        <p data-testid="admin-login-error" className="mt-3 text-sm text-[#FF2E4D]">
                            {loginErr}
                        </p>
                    )}
                    <button
                        data-testid="admin-login-btn"
                        onClick={login}
                        className="mt-5 w-full rounded-full bg-[#FFB800] py-3 font-semibold text-[#0B0B0E] transition-colors hover:bg-[#FF2E4D] hover:text-white"
                    >
                        {a.login}
                    </button>
                    <Link to="/" className="mt-5 flex items-center justify-center gap-2 text-xs text-[#8E8EA0] hover:text-[#FFB800]">
                        <ArrowLeft size={13} />
                        {a.backHome}
                    </Link>
                </div>
            </div>
        );
    }

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const list = bookings || [];
    const filtered = filter === "all" ? list : list.filter((b) => b.status === filter);
    const stats = [
        { label: a.total, value: list.length },
        { label: a.pending, value: list.filter((b) => b.status === "pending").length },
        { label: a.tonight, value: list.filter((b) => b.date === todayStr && ["pending", "confirmed"].includes(b.status)).length },
    ];

    return (
        <div className="min-h-screen bg-[#0B0B0E] px-4 py-8 sm:px-8" data-testid="admin-dashboard">
            <div className="mx-auto max-w-6xl">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <LogoMark size={40} />
                        <h1 className="font-display text-xl font-bold">{a.title}</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => load(token)}
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-[#8E8EA0] hover:border-[#FFB800]/50 hover:text-[#FFB800]"
                        >
                            <RefreshCw size={14} />
                        </button>
                        <Link to="/" className="rounded-full border border-white/15 px-4 py-2 text-sm text-[#8E8EA0] hover:text-[#FFB800]">
                            {a.backHome}
                        </Link>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="inline-flex items-center gap-2 rounded-full border border-[#FF2E4D]/40 px-4 py-2 text-sm text-[#FF2E4D] hover:bg-[#FF2E4D]/10"
                        >
                            <LogOut size={14} />
                            {a.logout}
                        </button>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-3 gap-4">
                    {stats.map((s) => (
                        <div key={s.label} className="rounded-2xl border border-white/10 bg-[#13131A] p-5">
                            <p className="font-mono2 text-3xl font-bold text-[#FFB800]">{s.value}</p>
                            <p className="mt-1 text-xs text-[#8E8EA0]">{s.label}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-8 rounded-2xl border border-white/10 bg-[#13131A] p-5" data-testid="admin-dayoff-panel">
                    <p className="font-display text-sm font-semibold text-[#FFB800]">{a.daysOff}</p>
                    <p className="mt-1 text-xs text-[#8E8EA0]">{a.daysOffNote}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <input
                            data-testid="admin-dayoff-input"
                            type="date"
                            value={offDate}
                            onChange={(e) => setOffDate(e.target.value)}
                            className="rounded-xl border border-white/10 bg-black/40 px-4 py-2 font-mono2 text-sm text-[#F4F4F6] outline-none focus:border-[#FFB800] [color-scheme:dark]"
                        />
                        <button
                            data-testid="admin-dayoff-add-btn"
                            onClick={addOff}
                            className="rounded-full bg-[#FF2E4D]/15 px-4 py-2 text-xs font-semibold text-[#FF2E4D] hover:bg-[#FF2E4D]/25"
                        >
                            {a.addOff}
                        </button>
                        {blocked.length === 0 && <span className="text-xs text-[#8E8EA0]">{a.offEmpty}</span>}
                        {blocked.map((d) => (
                            <span
                                key={d}
                                data-testid="admin-dayoff-chip"
                                className="inline-flex items-center gap-2 rounded-full border border-[#FF2E4D]/30 bg-[#FF2E4D]/5 px-3 py-1.5 font-mono2 text-xs text-[#FF2E4D]"
                            >
                                {d}
                                <button onClick={() => removeOff(d)} aria-label="remove" className="hover:text-white">
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-2">
                    {["all", "pending", "confirmed", "completed", "cancelled"].map((f) => (
                        <button
                            key={f}
                            data-testid={`admin-filter-${f}`}
                            onClick={() => setFilter(f)}
                            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                                filter === f
                                    ? "border-[#FFB800] bg-[#FFB800] font-semibold text-[#0B0B0E]"
                                    : "border-white/15 text-[#8E8EA0] hover:text-[#FFB800]"
                            }`}
                        >
                            {f === "all" ? a.filterAll : a.status[f]}
                        </button>
                    ))}
                </div>

                <div className="mt-6 space-y-3">
                    {filtered.length === 0 && (
                        <p className="rounded-2xl border border-white/10 bg-[#13131A] p-10 text-center text-[#8E8EA0]">{a.empty}</p>
                    )}
                    {filtered.map((b) => (
                        <div
                            key={b.id}
                            data-testid="admin-booking-row"
                            className="flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#13131A] p-5"
                        >
                            <div className="min-w-[140px]">
                                <p className="font-semibold text-[#F4F4F6]">{b.name}</p>
                                <p className="font-mono2 text-xs text-[#8E8EA0]">{b.phone}</p>
                                {b.email && <p className="font-mono2 text-xs text-[#8E8EA0]">{b.email}</p>}
                            </div>
                            <div className="min-w-[130px]">
                                <p className="text-sm text-[#c9c9d4]">{serviceName(b.service, lang)}</p>
                                <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#8E8EA0]">{b.reference}</p>
                            </div>
                            <p className="font-mono2 text-sm text-[#FFB800]">
                                {b.date} · {b.time_slot}
                            </p>
                            <span className={`rounded-full border px-3 py-1 font-mono2 text-xs ${statusColor[b.status]}`}>
                                {a.status[b.status]}
                            </span>
                            {b.notes && <p className="w-full text-xs text-[#8E8EA0] sm:w-auto sm:flex-1">{b.notes}</p>}
                            <div className="ml-auto flex flex-wrap gap-2">
                                {b.status === "pending" && (
                                    <button
                                        data-testid="admin-status-confirm-btn"
                                        onClick={() => act(b.id, "confirmed")}
                                        className="rounded-full bg-[#00F0FF]/15 px-3.5 py-1.5 text-xs font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/25"
                                    >
                                        {a.actions.confirm}
                                    </button>
                                )}
                                {["pending", "confirmed"].includes(b.status) && (
                                    <>
                                        <button
                                            onClick={() => act(b.id, "completed")}
                                            className="rounded-full bg-emerald-400/15 px-3.5 py-1.5 text-xs font-semibold text-emerald-400 hover:bg-emerald-400/25"
                                        >
                                            {a.actions.complete}
                                        </button>
                                        <button
                                            data-testid="admin-status-cancel-btn"
                                            onClick={() => act(b.id, "cancelled")}
                                            className="rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-[#c9c9d4] hover:bg-white/20"
                                        >
                                            {a.actions.cancel}
                                        </button>
                                    </>
                                )}
                                <button
                                    data-testid="admin-delete-btn"
                                    onClick={() => del(b.id)}
                                    className="rounded-full bg-[#FF2E4D]/10 p-2 text-[#FF2E4D] hover:bg-[#FF2E4D]/25"
                                    aria-label={a.actions.del}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
